/**
 * CreatureController — Heuristic Priority System + Minimax Fallback
 *
 * Shares the same 29-priority decision engine as Gaia (DionsAI),
 * but instead of falling back to Focused MCTS for complex positions,
 * it falls back to MinimaxEngine with alpha-beta pruning.
 *
 * The priority system prunes bad moves first, then minimax searches
 * the smaller remaining space — potentially going deeper within the
 * same time budget.
 */

import { GameState, IMove, Position } from "../../types";
import { MCTSDecisionController } from "../../MCTS_v2/decision-engine/MCTSDecisionController";
import { MoveInfo } from "../../MCTS_v2/decision-engine/interfaces/DecisionPriority";
import { MCTSMoveGenerationService } from "../../MCTS_v2/utils/MCTSMoveGenerationService";
import { ZodiacEngine } from "../../zodiacEngine";
import { MinimaxEngine, MinimaxModel, MoveWithScore, PVNode } from "../MinimaxAI/MinimaxEngine";
import { MoveInfo as CommonMoveInfo } from "../Core/CommonAIUtils";

export interface CreatureResult {
  fromPosition: Position;
  move: IMove;
  decisionType: "priority" | "minimax";
  reasoning: string;
  minimaxData?: {
    bestScore: number;
    depth: number;
    principalVariation?: PVNode[];
  };
}

export class CreatureController {
  private decisionEngine: MCTSDecisionController;
  private minimaxEngine: MinimaxEngine;
  private debugMode: boolean = false;
  private gameEngine: ZodiacEngine;
  private lastDecisionReasoning: string = "Priority-based decision";
  private lastDecisionType: "priority" | "minimax" = "minimax";
  private lastDecisionLogs: string[] = [];
  private lastMinimaxData: CreatureResult["minimaxData"] | undefined;

  constructor(gameEngine: ZodiacEngine, debugMode: boolean = false) {
    this.debugMode = debugMode;
    this.gameEngine = gameEngine;
    this.decisionEngine = new MCTSDecisionController(debugMode);
    this.minimaxEngine = new MinimaxEngine(gameEngine, MinimaxModel.SENTINEL);
  }

  /**
   * Main entry point — find the best move using priorities + minimax fallback
   */
  public findBestMove(maxThinkingTime: number): CreatureResult | null {
    const gameState = this.gameEngine.getGameState();
    const startTime = Date.now();

    // Reset logs
    this.lastDecisionLogs = ["🐾 Creature: Heuristics + Minimax Engine"];
    this.lastMinimaxData = undefined;

    // Step 1: Generate all possible moves
    const allPossibleMoves = this.getAllPossibleMoves(gameState);

    if (gameState.activeBonusTurnPieceId) {
      this.addLog(`🔄 BONUS TURN ACTIVE: Piece ID ${gameState.activeBonusTurnPieceId}`);
    }

    if (allPossibleMoves.length === 0) {
      this.addLog("❌ No legal moves available");
      return null;
    }

    this.addLog(`📋 Generated ${allPossibleMoves.length} possible moves`);

    // Step 2: Run the 29-priority decision engine (same as Gaia)
    const decision = this.decisionEngine.makeDecision(
      gameState,
      allPossibleMoves,
      gameState.turnCount,
      maxThinkingTime
    );

    // Collect logs from decision engine
    if (decision.diagnostics?.logs) {
      for (const log of decision.diagnostics.logs) {
        this.addLog(log);
      }
    }

    // Step 3: If priority system made a forced decision, use it
    if (decision.shouldEarlyExit && decision.selectedMove) {
      this.lastDecisionReasoning = `Priority ${decision.priority}: ${decision.reasoning}`;
      this.lastDecisionType = "priority";

      this.addLog(`⚡ EARLY EXIT: Priority ${decision.priority} — ${decision.reasoning}`);

      return {
        fromPosition: decision.selectedMove.fromPosition,
        move: decision.selectedMove.move,
        decisionType: "priority",
        reasoning: this.lastDecisionReasoning,
      };
    }

    // Step 4: Fall back to Minimax on filtered moves
    const movesToConsider = decision.filteredMoves || allPossibleMoves;
    const elapsedSoFar = Date.now() - startTime;
    const remainingTime = Math.max(1, maxThinkingTime - elapsedSoFar);

    this.addLog(
      `🧠 Minimax fallback: ${movesToConsider.length} moves (filtered from ${allPossibleMoves.length}), ${remainingTime}ms remaining`
    );

    // Convert MoveInfo to CommonMoveInfo (identical structure, different import paths)
    const rootMoves: CommonMoveInfo[] = movesToConsider.map(m => ({
      fromPosition: m.fromPosition,
      move: m.move,
    }));

    const minimaxResult = this.minimaxEngine.findBestMove(gameState, remainingTime, rootMoves);

    if (!minimaxResult) {
      // Minimax failed — pick first available move as safety fallback
      this.addLog("⚠️ Minimax returned null — using first available move");
      const fallback = movesToConsider[0];
      this.lastDecisionReasoning = "Minimax fallback (no result)";
      this.lastDecisionType = "minimax";

      return {
        fromPosition: fallback.fromPosition,
        move: fallback.move,
        decisionType: "minimax",
        reasoning: this.lastDecisionReasoning,
      };
    }

    // Get PV data from minimax
    const pv = MinimaxEngine.lastPV;
    this.lastMinimaxData = {
      bestScore: minimaxResult.score,
      depth: minimaxResult.depth || 0,
      principalVariation: pv.length > 0 ? pv : undefined,
    };

    this.lastDecisionReasoning = `Minimax (${movesToConsider.length} filtered moves, depth ${minimaxResult.depth || "?"}, score ${minimaxResult.score})`;
    this.lastDecisionType = "minimax";

    this.addLog(`🏆 Minimax selected: score=${minimaxResult.score}, depth=${minimaxResult.depth}`);

    return {
      fromPosition: minimaxResult.fromPosition,
      move: minimaxResult.move,
      decisionType: "minimax",
      reasoning: this.lastDecisionReasoning,
      minimaxData: this.lastMinimaxData,
    };
  }

  /**
   * Get analysis data for UI display (matches MCTSController_v2 interface)
   */
  public getLatestAnalysisDataDirectly(): any {
    const gameState = this.gameEngine.getGameState();
    const raceAnalysis = this.getSimpleRaceAnalysis(gameState);

    return {
      mctsPerformed: false,
      minimaxPerformed: this.lastDecisionType === "minimax",
      reasonForMove: "Creature: Heuristics + Minimax",
      chosenMove: this.lastDecisionReasoning,
      executionTime: 5,
      iterations: 0,
      nodesVisited: 0,
      preprocessingLog: this.lastDecisionLogs,
      topMovesConsidered: [],
      raceAnalysis,
      minimaxData: this.lastMinimaxData,
    };
  }

  public setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
    this.decisionEngine.setDebugMode(enabled);
  }

  private getAllPossibleMoves(gameState: GameState): MoveInfo[] {
    const moveService = MCTSMoveGenerationService.getInstance();
    const moves = moveService.getAllMoves(gameState);

    return moves.map((moveInfo) => ({
      fromPosition: moveInfo.fromPosition,
      move: moveInfo.move,
    }));
  }

  private addLog(message: string): void {
    this.lastDecisionLogs.push(message);
  }

  private getSimpleRaceAnalysis(gameState: any) {
    const ourKing = this.findKing(gameState, gameState.isLightTurn);
    const opponentKing = this.findKing(gameState, !gameState.isLightTurn);

    if (!ourKing || !opponentKing) {
      return {
        ourKingPosition: null,
        ourTurnsToWin: 0,
        opponentKingPosition: null,
        opponentTurnsToWin: 0,
      };
    }

    const ourDistance = gameState.isLightTurn ? 7 - ourKing.row : ourKing.row;
    const opponentDistance = !gameState.isLightTurn ? 7 - opponentKing.row : opponentKing.row;

    return {
      ourKingPosition: ourKing,
      ourTurnsToWin: Math.max(0, ourDistance),
      opponentKingPosition: opponentKing,
      opponentTurnsToWin: Math.max(0, opponentDistance),
    };
  }

  private findKing(gameState: any, isLight: boolean): { row: number; col: number } | null {
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isKing && piece.isLight === isLight) {
            return { row, col };
          }
        }
      }
    }
    return null;
  }
}
